<script lang="ts">import { onMount } from 'svelte';
import BodyScrollLocker from '../../shared/BodyScrollLocker/BodyScrollLocker.svelte';
import Chevron from '../../shared/Chevron/Chevron.svelte';
import Dropdown from './Dropdown/Dropdown.svelte';
import Link from '../../shared/Link/Link.svelte';
import { CTA_SUBNAV_MOBILE_DROPDOWN_MENU_ID, DROPDOWN_ENTER_DURATION } from '../../../../js-v2/constants';
import { mobileCtaSubnavMenuStateStore } from '../../../../js-v2/stores/mobile-cta-subnav-stores';
import { mobileNavStateStore } from '../../../../js-v2/stores/mobile-nav-stores';
import { mobileNavIsSticky } from '../../../../js-v2/stores/mobile-nav-sticky-store';
import { filterNavItems, shouldRenderCta, shouldRenderSecondaryCta } from '../../../../js-v2/navItemFilters';
import { onTouchOrClick } from '../../../../js-v2/onTouchOrClick';
import { getTracking } from '../../../../js-v2/tracking';
import { getLocation, getNavDeviceType } from '../../../../js-v2/trackingUtils';
import { getUrlPath } from '../../../../js-v2/urlUtils';
import { getLoggedIn, getCartCount } from '../../../../js-v2/stores/user-state-stores';
import { getCurrentUrl } from '../../../../js-v2/stores/url-stores';
const { id = '', title = '', navItems = [], primaryCtaButton = undefined, secondaryCtaButton = undefined, showCtaOnCtaPage = false, hiddenUntilScrolled = false, disableIconCtaOnMobile = false, isSticky = false, } = $props();
const cartCountStore = getCartCount();
const loggedInStore = getLoggedIn();
const currentUrlStore = getCurrentUrl();
const trackFn = getTracking();
// mobileNavHeight is bound to header height below
// and used to size the shim element
let mobileNavHeight = $state();
// mobileNavWidth is bound to nav width below
// and used to set the width of the header & footer
// (which they need when they are position fixed)
let mobileNavWidth = $state();
let ctaSubnavMobileHeader = $state();
// Determine whether or not to render CTA
const renderCta = $derived(shouldRenderCta({
    primaryCtaButton,
    showCtaOnCtaPage,
    currentUrl: $currentUrlStore,
    loggedIn: $loggedInStore,
    cartCount: $cartCountStore,
}));
// Filtered CTA item
const filteredPrimaryCta = $derived(renderCta ? primaryCtaButton : undefined);
// Determine whether or not to render Secondary CTA
const renderSecondaryCta = $derived(shouldRenderSecondaryCta({
    secondaryCtaButton,
    loggedIn: $loggedInStore,
    cartCount: $cartCountStore,
}));
const disableLeftCtaIcon = $derived(disableIconCtaOnMobile || !filteredPrimaryCta?.props?.icon); // disable left cta icon if the config is set to true OR if there is no icon provided
// Filtered Secondary CTA item
const filteredSecondaryCta = $derived(renderSecondaryCta ? secondaryCtaButton : undefined);
// Filtered nav items
const filteredNavItems = $derived(filterNavItems({
    items: navItems,
    loggedIn: $loggedInStore,
    cartCount: $cartCountStore,
}));
$effect(() => {
    mobileCtaSubnavMenuStateStore.setHasNavLinks(filteredNavItems.length > 0);
});
function handleToggleTouchOrClick() {
    if ($mobileCtaSubnavMenuStateStore.isOpen) {
        trackFn({
            eventName: 'Close Menu',
            properties: {
                currentPage: getUrlPath(),
                menuId: id,
                menuText: title,
                menuLocation: getLocation(ctaSubnavMobileHeader),
                navDeviceType: getNavDeviceType(ctaSubnavMobileHeader),
            },
        });
    }
    else {
        trackFn({
            eventName: 'Open Drawer',
            properties: {
                currentPage: getUrlPath(),
                menuId: id,
                menuText: title,
                menuLocation: getLocation(ctaSubnavMobileHeader),
                navDeviceType: getNavDeviceType(ctaSubnavMobileHeader),
            },
        });
    }
    mobileCtaSubnavMenuStateStore.toggle();
}
onMount(() => {
    // Resetting the position styling fixes the sticky nav not 'sticking' caused by deferred JS
    // Context: https://jira.sqprod.co/browse/WEBENG-4190
    if (ctaSubnavMobileHeader) {
        ctaSubnavMobileHeader.style.position = '';
    }
});
</script>

<nav class="MobileNav" bind:offsetWidth={mobileNavWidth} data-cta-subnav-mobile-v2>
  {#if title}
    <!-- this shim only have an impact when !hiddenUntilScrolled because if hiddenUntilScrolled is true, the shim is added by Waypoint component -->
    <div
      class="shim"
      aria-hidden="true"
      style:height={isSticky && !hiddenUntilScrolled ? `${mobileNavHeight}px` : '0px'}
    ></div>
    <div class="transition-shim" aria-hidden="true" class:globalNavIsSticky={$mobileNavIsSticky}></div>
    <div
      class="cta-subnav-menu-container"
      class:isSticky
      class:globalNavIsSticky={$mobileNavIsSticky}
      class:hiddenUntilScrolled
      style:width={`${mobileNavWidth}px`}
      data-entry-id={id}
      bind:offsetHeight={mobileNavHeight}
      bind:this={ctaSubnavMobileHeader}
    >
      {#if filteredNavItems?.length > 0}
        <button
          class="dropdown-toggle"
          aria-haspopup="true"
          aria-expanded={$mobileCtaSubnavMenuStateStore.isOpen}
          aria-controls={CTA_SUBNAV_MOBILE_DROPDOWN_MENU_ID}
          use:onTouchOrClick={handleToggleTouchOrClick}
        >
          <span class="title">{title}</span>
          <Chevron isOpen={$mobileCtaSubnavMenuStateStore.isOpen} />
        </button>
      {:else}
        <span class="title">{title}</span>
      {/if}
    </div>
    {#if filteredNavItems}
      <div id={CTA_SUBNAV_MOBILE_DROPDOWN_MENU_ID}>
        <Dropdown navItems={filteredNavItems} {mobileNavWidth} />
      </div>
    {/if}
  {/if}

  {#if filteredPrimaryCta || filteredSecondaryCta}
    <div
      class="cta-button-container"
      class:isSticky
      class:isVisible={isSticky || $mobileCtaSubnavMenuStateStore.isOpen}
      class:disableLeftCtaIcon
      style:width={`${mobileNavWidth}px`}
    >
      {#if filteredSecondaryCta}
        <Link
          id={filteredSecondaryCta?.contentfulEntryId}
          title={filteredSecondaryCta?.props?.title}
          url={filteredSecondaryCta?.props?.url}
          linkStyle={filteredSecondaryCta?.props?.linkStyle}
          icon={filteredSecondaryCta?.props?.icon}
          openInNewTab={filteredSecondaryCta?.props?.openInNewTab}
        />
      {/if}
      {#if filteredPrimaryCta}
        <Link
          id={filteredPrimaryCta?.contentfulEntryId}
          title={filteredPrimaryCta?.props?.title}
          url={filteredPrimaryCta?.props?.url}
          linkStyle={filteredPrimaryCta?.props?.linkStyle}
          icon={filteredPrimaryCta?.props?.icon}
          openInNewTab={filteredPrimaryCta?.props?.openInNewTab}
        />
      {/if}
    </div>
  {/if}
</nav>

<!-- pass in DROPDOWN_ENTER_DURATION to delayLockAfter, the value is the same as $duration-medium-in -->
<BodyScrollLocker
  shouldLock={$mobileCtaSubnavMenuStateStore.isOpen || $mobileNavStateStore.isOpen}
  delayLockAfter={DROPDOWN_ENTER_DURATION}
/>

<style>@keyframes fade-in-slide-from-top {
  from {
    opacity: 0;
    transform: translateX(0) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes fade-in-slide-from-left {
  from {
    opacity: 0;
    transform: translateX(-4px) translateY(0);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes slide-down {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(0);
  }
}
/**
We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
*/
.MobileNav {
  display: block;
}
@media (min-width: 80rem) {
  .MobileNav {
    display: none;
  }
}

.shim {
  background-color: var(--color-background, var(--color-neutral-1000, #fff));
}

.transition-shim {
  display: block;
  position: fixed;
  top: 0;
  width: 100vw;
  background-color: var(--color-background, var(--color-neutral-1000, #fff));
  height: 0px;
  transition: height 300ms cubic-bezier(0, 0, 0.2, 1);
}
@media (min-width: 64rem) {
  .transition-shim {
    display: none;
  }
}
.transition-shim.globalNavIsSticky {
  height: 72px;
  transition: height 0s cubic-bezier(0, 0, 0.2, 1);
}

.cta-button-container,
.cta-subnav-menu-container {
  padding-left: 32px;
  padding-right: 32px;
  box-sizing: border-box;
  position: relative;
  min-width: 320px;
  height: 72px;
  z-index: 1;
  padding-top: 4px;
  padding-bottom: 4px;
  background-color: var(--color-background, var(--color-neutral-1000, #fff));
  color: var(--color-text, var(--color-neutral-000, #000000));
  border-bottom: 1px solid var(--color-border, var(--color-neutral-700, #d3d3d3));
  display: flex;
  align-items: center;
}
@media (min-width: 105rem) {
  .cta-button-container,
  .cta-subnav-menu-container {
    --nav-outer-gutter-base: calc(var(--space-10) / 2);
    --nav-outer-gutter: max(var(--nav-outer-gutter-base), var(--dynamic-gutter));
    padding-left: calc(32px + var(--nav-outer-gutter)) !important;
    padding-right: calc(32px + var(--nav-outer-gutter)) !important;
  }
}

.cta-subnav-menu-container .dropdown-toggle {
  --letter-spacing: var(--text-variant-100-letter-spacing);
  --line-height: var(--text-variant-100-line-height);
  --font-size: var(--text-variant-100-font-size);
  --font-weight: var(--text-variant-100-font-weight);
  --font-family: var(--text-font-family);
  --max-width: var(--text-variant-100-max-width);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  letter-spacing: var(--letter-spacing);
  line-height: var(--line-height);
  max-width: var(--max-width);
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  display: inline-block;
  width: auto;
  height: auto;
  min-height: 0;
  max-height: none;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 10px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--color-text, var(--color-neutral-000, #000000));
  letter-spacing: normal;
  text-align: center;
  vertical-align: middle;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  --letter-spacing: var(--text-variant-100-letter-spacing);
  --line-height: var(--text-variant-100-line-height);
  --font-size: var(--text-variant-100-font-size);
  --font-weight: var(--text-variant-100-font-weight);
  --font-family: var(--text-font-family);
  --max-width: var(--text-variant-100-max-width);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  letter-spacing: var(--letter-spacing);
  line-height: var(--line-height);
  max-width: var(--max-width);
  padding: 2px 0px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.cta-subnav-menu-container .dropdown-toggle:focus-visible:not(:active) {
  outline: var(--accessibility-focus-outline-width) var(--accessibility-focus-outline-style, solid) var(--accessibility-focus-outline-color);
  border-radius: var(--accessibility-focus-outline-border-radius);
  outline-offset: var(--accessibility-focus-outline-offset);
}
.cta-subnav-menu-container .dropdown-toggle:focus {
  background: transparent;
  outline: none;
}
.cta-subnav-menu-container .dropdown-toggle:hover {
  background: transparent;
}
.cta-subnav-menu-container .title {
  --letter-spacing: var(--text-variant-100-letter-spacing);
  --line-height: var(--text-variant-100-line-height);
  --font-size: var(--text-variant-100-font-size);
  --font-weight: var(--text-variant-100-font-weight);
  --font-family: var(--text-font-family);
  --max-width: var(--text-variant-100-max-width);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  letter-spacing: var(--letter-spacing);
  line-height: var(--line-height);
  max-width: var(--max-width);
  display: inline-block;
  padding: 10px 0;
  color: var(--color-text, var(--color-neutral-000, #000000));
}
.cta-subnav-menu-container.isSticky {
  position: fixed;
  top: 0;
  border-bottom: 1px solid var(--color-border);
  transform: translateY(0);
  transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
}
@media (max-width: 63.9375rem) {
  .cta-subnav-menu-container.isSticky.globalNavIsSticky {
    transition: transform 100ms cubic-bezier(0, 0, 0.2, 1);
    transform: translateY(100%);
  }
}

.cta-button-container {
  z-index: 4;
  flex-flow: row-reverse;
  position: fixed;
  bottom: 0;
  gap: 20px;
  padding: 16px 20px;
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  transform: translateY(100%);
  transition: transform 75ms cubic-bezier(0.4, 0, 1, 1);
}
.cta-button-container.isVisible {
  transform: translateY(0%);
  transition: transform 150ms cubic-bezier(0, 0, 0.2, 1);
}

.hiddenUntilScrolled {
  position: fixed;
  z-index: 2;
  top: 0;
  transition: transform 200ms cubic-bezier(0.4, 0, 1, 1);
  transform: translateY(-100%);
}

:global(.cta-button-container) :global(.Link) :global(.title) {
  display: none;
}
:global(.cta-button-container) :global(.Link:first-child) {
  flex: 1;
}
:global(.cta-button-container) :global(.Link:first-child) :global(.title) {
  display: inline;
}
:global(.cta-button-container) :global(.Link:first-child) :global(.icon) {
  display: none;
}
@media (min-width: 64rem) {
  :global(.cta-button-container) :global(.Link) {
    flex: 1;
  }
  :global(.cta-button-container) :global(.Link) :global(.icon) {
    display: none;
  }
  :global(.cta-button-container) :global(.Link) :global(.title) {
    display: inline;
  }
}
:global(.cta-button-container.disableLeftCtaIcon) {
  gap: 7px;
}
:global(.cta-button-container.disableLeftCtaIcon) :global(.Link:last-child) {
  flex: 1;
}
:global(.cta-button-container.disableLeftCtaIcon) :global(.Link:first-child) {
  flex: 1;
}
:global(.cta-button-container.disableLeftCtaIcon) :global(.Link) :global(.title) {
  display: inline;
}
:global(.cta-button-container.disableLeftCtaIcon) :global(.Link) :global(.icon) {
  display: none;
}</style>
